// src/components/SeoSettings.jsx
//
// Meta title/description overrides for each resort's guest-site microsite
// page - its own menu item and permission (manage_seo), separate from
// Website Content, because tuning search-result copy is a different job
// from staff writing guest-facing marketing text, even though both end up
// on the same page. See Resort.seoTitle/seoDescription's javadoc on the
// backend and ResortSeoRequest for why this has its own narrow save
// endpoint rather than sharing Website Content's save-everything one.
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import { fetchResorts, fetchResortById, updateResortSeo } from "../services/ResortDetailsService";
import "./WebsiteContent.css";

const SITE_URL = "https://vintarastays.in";

// Mirrors app/lib/slug.js's slugify on the guest site - only used here to
// show staff which live URL they're editing, never sent anywhere or relied
// on for a save.
function slugify(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const TITLE_MAX = 70;
const DESCRIPTION_MAX = 200;

const SeoSettings = () => {
  const [resorts, setResorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toasts, showToast, dismissToast } = useToast();

  const [selectedResort, setSelectedResort] = useState(null);
  const [resortDetail, setResortDetail] = useState(null); // for the URL/fallback preview only
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");

  const loadResorts = async () => {
    try {
      setLoading(true);
      const data = await fetchResorts();
      setResorts(data || []);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load resorts", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resortOptions = useMemo(
    () => resorts.map((r) => ({ value: r.id, label: r.name })),
    [resorts]
  );

  useEffect(() => {
    if (!selectedResort) return;
    let cancelled = false;
    setLoadingDetail(true);
    fetchResortById(selectedResort.value)
      .then((resort) => {
        if (cancelled || !resort) return;
        setResortDetail(resort);
        setSeoTitle(resort.seoTitle || "");
        setSeoDescription(resort.seoDescription || "");
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        showToast(err.message || "Failed to load resort details", "danger");
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResort]);

  const handleSave = async () => {
    if (!selectedResort) return;
    setSaving(true);
    try {
      await updateResortSeo(selectedResort.value, {
        seoTitle: seoTitle.trim() || null,
        seoDescription: seoDescription.trim() || null,
      });
      showToast("SEO settings saved", "success");
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to save SEO settings", "danger");
    } finally {
      setSaving(false);
    }
  };

  const resortUrl = resortDetail
    ? `${SITE_URL}/stays/${slugify(resortDetail.location)}/${resortDetail.slug}`
    : null;

  // What the page will actually show if these fields are left blank -
  // mirrors GuestController's effectiveSeoTitle/effectiveSeoDescription
  // fallback (name/location, then tagline) closely enough to preview, but
  // is not a byte-for-byte guarantee - the backend's computed value is the
  // real source of truth.
  const fallbackTitle = resortDetail
    ? resortDetail.location
      ? `${resortDetail.name}, ${resortDetail.location} — Vintara Stays`
      : `${resortDetail.name} — Vintara Stays`
    : "";
  const fallbackDescription = resortDetail
    ? resortDetail.tagline || `Book ${resortDetail.name} direct${resortDetail.location ? ` in ${resortDetail.location}` : ""} — handpicked by Vintara Stays. No middleman, no markup.`
    : "";

  const previewTitle = seoTitle.trim() || fallbackTitle;
  const previewDescription = seoDescription.trim() || fallbackDescription;

  return (
    <div className="resorts-page-container website-content-page" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      <div className="vt-page-header">
        <div>
          <h2>SEO Settings</h2>
          <p className="field-hint website-content-intro">
            Override the search-result title and description for one resort&apos;s guest-site page. Leave either
            blank to use an automatically generated one instead - every resort already has a reasonable default,
            this is only for fine-tuning.
          </p>
        </div>
      </div>

      <div className="website-content-selector">
        <Select
          options={resortOptions}
          value={selectedResort}
          onChange={setSelectedResort}
          placeholder={loading ? "Loading resorts..." : "Select a resort..."}
          isLoading={loading}
          menuPortalTarget={menuPortalTarget}
          menuPosition={menuPosition}
          styles={themedSelectStyles()}
        />
      </div>

      {selectedResort && loadingDetail && <p className="field-hint">Loading resort details...</p>}

      {selectedResort && !loadingDetail && resortDetail && (
        <>
          {resortUrl && (
            <p className="field-hint" style={{ marginBottom: 18 }}>
              Editing: <a href={resortUrl} target="_blank" rel="noopener noreferrer">{resortUrl}</a>
            </p>
          )}

          <div className="resort-details-section">
            <h4>Search preview</h4>
            <div style={{ border: "1px solid #e8e0d0", borderRadius: 10, padding: "14px 16px", maxWidth: 560, background: "#fff" }}>
              <div style={{ fontSize: 12.5, color: "#3d7a3d", marginBottom: 2 }}>{resortUrl}</div>
              <div style={{ fontSize: 17, color: "#1a0dab", lineHeight: 1.3, marginBottom: 3 }}>{previewTitle}</div>
              <div style={{ fontSize: 13, color: "#4d5156", lineHeight: 1.45 }}>{previewDescription}</div>
            </div>
          </div>

          <div className="resort-details-section">
            <h4>Meta Title</h4>
            <div className="form-field">
              <input
                type="text"
                placeholder={fallbackTitle}
                value={seoTitle}
                maxLength={TITLE_MAX}
                onChange={(e) => setSeoTitle(e.target.value)}
              />
              <p className="field-hint">{seoTitle.length}/{TITLE_MAX} characters. Shown as the blue link text in Google and as the browser tab title.</p>
            </div>
          </div>

          <div className="resort-details-section">
            <h4>Meta Description</h4>
            <div className="form-field">
              <textarea
                rows={3}
                placeholder={fallbackDescription}
                value={seoDescription}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => setSeoDescription(e.target.value)}
              />
              <p className="field-hint">{seoDescription.length}/{DESCRIPTION_MAX} characters. Shown as the gray summary text under the title in search results.</p>
            </div>
          </div>

          <div className="button-group">
            <button type="button" className="vt-btn vt-btn-primary" disabled={saving} onClick={handleSave}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SeoSettings;
